import {
  AfterViewInit,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  computed,
  inject,
  input,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@app/pipes/translate.pipe';

import { Blog } from '@models/blog.model';
import { Category } from '@models/category/Category.model';
import { HowTo } from '@models/guides/howToGuide.model';
import { PriceGuide } from '@models/guides/priceGuide.model';
import { Tip } from '@models/guides/tips.model';

import { Vertical } from '@models/vertical/vertical.model';
import { generateEntityUrl } from '@helpers/generateEntityUrl';
import { indexPagePath } from '@helpers/indexPagePath';
import { CardLoaderComponent } from '@components/shared/loaders/card-loader/card-loader.component';
import { HeaderLoaderComponent } from '@components/shared/loaders/header-loader/header-loader.component';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NgOptimizedImage } from '@angular/common';

type EntityData = (
  | HowTo
  | Blog
  | Category
  | PriceGuide
  | Tip
  | Vertical
  | any
)[];
// MARK: // TODO:// Fix this type

@Component({
  imports: [
    CardLoaderComponent,
    CommonModule,
    HeaderLoaderComponent,
    MatIconModule,
    NgOptimizedImage,
    RouterModule,
    TranslatePipe,
  ],
  selector: 'app-generic-card',
  templateUrl: './generic-card.component.html',
  styleUrls: ['./generic-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { ngSkipHydration: 'true' },
})
export class GenericCardComponent implements OnDestroy, AfterViewInit {
  protected readonly environment = inject(EnvToken);
  protected readonly seoSegments = inject(SeoSegmentsToken);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  public readonly entityData = input<EntityData>([]);
  public readonly dataIsLoading = input<boolean>(true);
  public readonly borderRadius = input<string>('30');
  public readonly numberOfCards = input<number>(3);
  public readonly headCategory = input<string>();
  public readonly subCategory = input<string | null>();
  public readonly subSubCategory = input<string | null>();
  public readonly title = input<string>('');
  public readonly isTipsAndTricks = input<boolean>(false);
  public readonly description = input<string>();
  public readonly maxImgHeight = input<number>();
  public readonly isToDashboard = input<boolean>();
  public readonly displayMode = input<
    'rows' | 'cols' | 'grid1' | 'grid2' | 'grid3' | 'grid4' | 'full-width'
  >();

  private readonly scrollContainerRef =
    viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly scrollableRef =
    viewChild<ElementRef<HTMLElement>>('scrollable');

  private resizeObserver: ResizeObserver | undefined;
  protected readonly isDisabled = signal<boolean>(false);

  protected readonly showLeft = signal<boolean>(true);
  protected readonly showRight = signal<boolean>(true);
  protected readonly tipsIndexLink = computed(() =>
    indexPagePath(this.seoSegments.indexPage.tips, [
      this.headCategory(),
      this.subCategory(),
      this.subSubCategory(),
    ]),
  );

  private isDragging: boolean = false;
  private lastX: number = 0;
  // Stable bound refs — add/removeEventListener must receive the SAME function
  // object, so binding inline at each call site would never unregister.
  private readonly mouseUpHandler = this.onMouseUp.bind(this);
  private readonly mouseEnterHandler = this.onMouseEnter.bind(this);
  private timeoutIds: NodeJS.Timeout[] = [];
  private animationFrameIds: number[] = [];

  private get scrollContainer() {
    return this.scrollContainerRef();
  }

  private get scrollable() {
    return this.scrollableRef();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      // Wait for the view to be fully initialized and hydrated
      const timeoutId = setTimeout(() => {
        this.setupResizeObserver();
        this.updateScrollButtons();
      }, 100); // Small delay to ensure DOM is ready
      this.timeoutIds.push(timeoutId);
    }
  }

  ngOnDestroy() {
    // Clean up all timeouts
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds = [];

    // Clean up all animation frames
    this.animationFrameIds.forEach((id) => cancelAnimationFrame(id));
    this.animationFrameIds = [];

    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Clean up mouse event listeners
    if (this.isBrowser) {
      this.document.removeEventListener('mouseup', this.mouseUpHandler);
      this.scrollable?.nativeElement?.removeEventListener(
        'mouseenter',
        this.mouseEnterHandler,
      );
    }
  }

  @HostListener('window:resize')
  protected onResize(): void {
    if (this.isBrowser) {
      this.setupResizeObserver();
      this.updateScrollButtons();
    }
  }

  protected onMouseDown(event: MouseEvent): void {
    if (this.isBrowser) {
      this.isDragging = true;
      this.lastX = event.clientX;
      if (this.scrollable?.nativeElement) {
        this.scrollable.nativeElement?.classList?.add('grabbing');
      }
      event.preventDefault();
      this.document.addEventListener('mouseup', this.mouseUpHandler);
    }
  }

  protected onMouseMove(event: MouseEvent): void {
    if (!this.isBrowser) {
      return;
    }
    this.isDisabled.set(this.isDragging);
    if (!this.isDragging) {
      return;
    }
    const deltaX = this.lastX - event.clientX;
    if (this.scrollable?.nativeElement) {
      this.scrollable.nativeElement.scrollLeft += deltaX;
    }
    this.lastX = event.clientX;
  }

  protected onMouseUp(): void {
    if (this.isBrowser) {
      this.isDragging = false;
      if (this.scrollable?.nativeElement) {
        this.scrollable.nativeElement?.classList.remove('grabbing');
      }
      this.document.removeEventListener('mouseup', this.mouseUpHandler);
    }
  }

  protected onMouseLeave(): void {
    if (this.isBrowser && this.scrollable?.nativeElement) {
      // Add a listener for mouseenter when the mouse leaves the scrollable area
      this.scrollable.nativeElement.addEventListener(
        'mouseenter',
        this.mouseEnterHandler,
      );
    }
  }

  private onMouseEnter(event: MouseEvent): void {
    if (this.isBrowser) {
      // If the mouse is not pressed when it reenters the scrollable area, stop dragging
      if (!event.buttons) {
        this.onMouseUp();
      }
      if (this.scrollable?.nativeElement) {
        this.scrollable.nativeElement.removeEventListener(
          'mouseenter',
          this.mouseEnterHandler,
        );
      }
    }
  }

  private setupResizeObserver() {
    if (this.isBrowser) {
      // Clean up any existing observer before creating a new one
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === this.scrollContainer?.nativeElement) {
            this.updateScrollButtons();
          }
        }
      });

      // Make sure the element exists before observing
      if (this.scrollContainer?.nativeElement) {
        try {
          this.resizeObserver.observe(this.scrollContainer.nativeElement);
        } catch (error) {}
      }
    }
  }

  protected scrollRight() {
    // First ensure elements are initialized
    this.initializeScrollElements();

    if (this.isBrowser && this.scrollContainer?.nativeElement) {
      try {
        const scrollAmount = 550;
        const animationFrameId = requestAnimationFrame(() => {
          if (this.scrollContainer?.nativeElement) {
            this.scrollContainer.nativeElement.scrollBy({
              left: scrollAmount,
              behavior: 'smooth',
            });

            const timeoutId = setTimeout(() => {
              this.updateScrollButtons();
            }, 200);
            this.timeoutIds.push(timeoutId);
          }
        });
        this.animationFrameIds.push(animationFrameId);
      } catch (error) {}
    }
  }

  protected scrollLeft() {
    // First ensure elements are initialized
    this.initializeScrollElements();

    if (this.isBrowser && this.scrollContainer?.nativeElement) {
      try {
        const scrollAmount = 550;
        const animationFrameId = requestAnimationFrame(() => {
          if (this.scrollContainer?.nativeElement) {
            this.scrollContainer.nativeElement.scrollBy({
              left: -scrollAmount,
              behavior: 'smooth',
            });
            this.updateScrollButtons();
          }
        });
        this.animationFrameIds.push(animationFrameId);
      } catch (error) {}
    }
  }

  protected updateScrollButtons() {
    if (this.isBrowser && this.scrollContainer?.nativeElement) {
      const animationFrameId = requestAnimationFrame(() => {
        const element = this.scrollContainer?.nativeElement;
        if (element) {
          const buffer = 2;
          this.showLeft.set(element.scrollLeft > buffer);
          this.showRight.set(
            Math.ceil(element.scrollLeft + element.clientWidth) <
              element.scrollWidth - buffer,
          );
        }
      });
      this.animationFrameIds.push(animationFrameId);
    }
  }

  protected generateUrl(
    entity: HowTo | Vertical | PriceGuide | Blog | Tip,
  ): string {
    return generateEntityUrl(
      this.seoSegments,
      entity,
      entity.mainCategory?.slug || this.headCategory(),
      this.subCategory(),
      this.subSubCategory(),
    );
  }

  protected getTrackById(item: any): string | number {
    return item.id || item._id || new Date();
  }

  private initializeScrollElements(): void {
    if (
      this.isBrowser &&
      (!this.scrollContainer?.nativeElement || !this.scrollable?.nativeElement)
    ) {
      // Force a refresh of the scroll elements
      const timeoutId = setTimeout(() => {
        this.setupResizeObserver();
        this.updateScrollButtons();
      }, 200);
      this.timeoutIds.push(timeoutId);
    }
  }
}
